// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/hooks/useStatusOs.ts
//  Cadastro de Status da Ordem de Serviço + combo p/ a aba Status da OS.
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { statusOsService } from '../services/statusOsService';
import type { SalvarStatusOrdemServicoRequest } from '../types';

export const useStatusOs = () => {
  const queryClient = useQueryClient();

  const { data: statusList = [], isLoading } = useQuery({
    queryKey: ['status-os'],
    queryFn: () => statusOsService.getAll(),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['status-os'] });
    queryClient.invalidateQueries({ queryKey: ['status-os-combo'] });
  };

  const createMutation = useMutation({
    mutationFn: (req: SalvarStatusOrdemServicoRequest) => statusOsService.create(req),
    onSuccess: () => { invalidate(); toast.success('Status criado'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarStatusOrdemServicoRequest }) =>
      statusOsService.update(id, req),
    onSuccess: () => { invalidate(); toast.success('Status atualizado'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => statusOsService.delete(id),
    onSuccess: () => { invalidate(); toast.success('Status removido'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return {
    statusList,
    isLoading,
    criarStatus: createMutation.mutate,
    atualizarStatus: updateMutation.mutate,
    removerStatus: deleteMutation.mutate,
    isSaving: createMutation.isPending || updateMutation.isPending,
  };
};

/** Combo de status ativos (para a aba Status da OS). */
export const useStatusOsCombo = () => {
  const { data: statusCombo = [], isLoading } = useQuery({
    queryKey: ['status-os-combo'],
    queryFn: () => statusOsService.getCombo(),
  });
  return { statusCombo, isLoading };
};
